import type Response from '../../../../api/types/Response';
import type Helper from '../../../models/Helper';

type OrganizationOrTeamId = string | number;

export default function withBulkedInvitations<T>(
  WrappedHelper: new (...args: ConstructorParameters<typeof Helper>) => Helper<T>
): new (...args: ConstructorParameters<typeof Helper>) => Helper<T> {
  const RESEND_ENDPOINT = '/invitations/resend';
  const DELETE_ENDPOINT = '/invitations';

  class WithBulkedInvitations extends WrappedHelper {
    protected resendInvitationByIds(
      entityId: OrganizationOrTeamId,
      bulkedIds: OrganizationOrTeamId[]
    ): Promise<Response> {
      const request = super.create({ ids: bulkedIds }, false);
      request.url += RESEND_ENDPOINT;
      request.setRouteParam('id', `${entityId}`);

      return request.execute();
    }

    protected resendInvitationByFilters(
      entityId: OrganizationOrTeamId,
      filters: Record<string, any>
    ): Promise<Response> {
      const request = super.create({}, false);
      request.url += RESEND_ENDPOINT;
      request.setRouteParam('id', `${entityId}`);
      request.setQueryParams(filters);

      return request.execute();
    }

    protected deleteInvitationByIds(
      entityId: OrganizationOrTeamId,
      bulkedIds: OrganizationOrTeamId[]
    ): Promise<Response> {
      const request = super.delete(entityId, false);
      request.url += DELETE_ENDPOINT;
      request.setQueryParam('ids', bulkedIds.join(','));

      return request.execute();
    }

    protected deleteInvitationByFilters(
      entityId: OrganizationOrTeamId,
      filters: Record<string, any>
    ): Promise<Response> {
      const request = super.delete(entityId, false);
      request.url += DELETE_ENDPOINT;
      request.setQueryParams(filters);

      return request.execute();
    }

    resendInvitations(
      entityId: OrganizationOrTeamId,
      options: OrganizationOrTeamId[] | Record<string, any>
    ): Promise<Response> {
      if (Array.isArray(options)) {
        return this.resendInvitationByIds(entityId, options);
      }

      return this.resendInvitationByFilters(entityId, options);
    }

    deleteInvitations(
      entityId: OrganizationOrTeamId,
      options: OrganizationOrTeamId[] | Record<string, any>
    ): Promise<Response> {
      if (Array.isArray(options)) {
        return this.deleteInvitationByIds(entityId, options);
      }

      return this.deleteInvitationByFilters(entityId, options);
    }
  }

  return WithBulkedInvitations;
}
